/**
 * Leningen - Openstaande Leningen overzicht
 * EXACT zoals Morris leningen.html
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, LeningenData } from '../../services/boekhouding.service';

const Leningen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LeningenData | null>(null);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await boekhoudingService.getLeningen(huidigJaar);
        setData(result);
      } catch (err) {
        console.error('Error loading leningen:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar]);

  const formatBedrag = (bedrag: number | null): string => {
    if (bedrag === null || bedrag === 0) return '';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(bedrag);
  };

  const formatDatum = (datum: string): string => {
    return new Date(datum).toLocaleDateString('nl-NL');
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Openstaande Leningen ${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Openstaande Leningen ${huidigJaar}`}>
        <div className={styles.error}>Kon leningen data niet laden</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title={`Openstaande Leningen ${huidigJaar}`}>
      {/* Leningen tabel */}
      <div className={styles.card}>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Bkst.nr.</th>
                <th>Datum</th>
                <th>Omschrijving</th>
                <th className={styles.alignRight}>Credit</th>
                <th className={styles.alignRight}>Debet</th>
              </tr>
            </thead>
            <tbody>
              {/* Transport row - EXACT Morris */}
              <tr>
                <td></td>
                <td></td>
                <td>Transport</td>
                <td className={styles.alignRight}>€ 0,00</td>
                <td className={styles.alignRight}></td>
              </tr>
              {data.leningen.length > 0 ? (
                data.leningen.map((l) => (
                  <tr
                    key={l.id}
                    className={styles.clickableRow}
                    onDoubleClick={() => navigate(`/boekhouding/boeking/${l.id}`)}
                    title="Dubbelklik om te bewerken"
                  >
                    <td>{l.boekstuknummer || '-'}</td>
                    <td>{formatDatum(l.datum)}</td>
                    <td>{l.omschrijving}</td>
                    <td className={styles.alignRight}>{l.credit ? formatBedrag(l.credit) : ''}</td>
                    <td className={styles.alignRight}>{l.debet ? formatBedrag(l.debet) : ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className={styles.emptyState}>Geen leningen gevonden</td>
                </tr>
              )}
            </tbody>
            {data.leningen.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}></td>
                  <td className={styles.alignRight}><strong>========</strong></td>
                  <td className={styles.alignRight}><strong>========</strong></td>
                </tr>
                <tr>
                  <td colSpan={3}></td>
                  <td className={styles.alignRight}><strong>{formatBedrag(data.totaal_credit)}</strong></td>
                  <td className={styles.alignRight}><strong>{formatBedrag(data.totaal_debet)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Verdichting tabel - EXACT Morris */}
      {data.verdichting && data.verdichting.length > 0 && (
        <div className={styles.card}>
          <h3>Leningen {huidigJaar} verdichting</h3>
          <table className={`${styles.dataTable} ${styles.verdichtingTable}`}>
            <thead>
              <tr>
                <th>Schuldeiser</th>
                <th className={styles.alignRight}>Credit</th>
                <th className={styles.alignRight}>Debet</th>
                <th className={styles.alignRight}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.verdichting.map((v, idx) => (
                <tr key={idx}>
                  <td>{v.naam}</td>
                  <td className={styles.alignRight}>{formatBedrag(v.credit)}</td>
                  <td className={styles.alignRight}>{formatBedrag(v.debet)}</td>
                  <td className={styles.alignRight}>{formatBedrag(v.saldo)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td className={styles.alignRight}><strong>========</strong></td>
                <td className={styles.alignRight}><strong>========</strong></td>
                <td className={styles.alignRight}><strong>========</strong></td>
              </tr>
              <tr>
                <td></td>
                <td className={styles.alignRight}><strong>{formatBedrag(data.totaal_credit)}</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(data.totaal_debet)}</strong></td>
                <td className={styles.alignRight}><strong>{formatBedrag(data.eind_saldo)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </BoekhoudingLayout>
  );
};

export default Leningen;
